import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box, Divider, SelectChangeEvent, Snackbar, Stack } from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import "../../assets/style.css";
import ConfigLoad from "../../components/shared/config/ConfigLoad";
import ControlsList from "../../components/configuration/otherControls/ControlsList";
import ControlDetails from "../../components/configuration/otherControls/ControlDetails";
import TabControl from "../../components/configuration/otherControls/TabControl";
import EncompassService from "../../services/encompassService";
import { IConfigurationIdEntry, IOtherControl } from "../../models/configuration/plugin/IConfiguration";
import { RootState, store } from "../../state/store";
import { useSelector } from "react-redux";
import { ConfigurationDataType, useConfigurationData } from "../../hooks/useConfigurationData";
import { useActions } from "../../hooks/useActions";
import { AlertSeverity } from "../../constants/AlertTypes";
import { useConfigurationSave } from "../../hooks/useConfigurationSave";
import { ActionType } from "../../state/actions";
import { DtPageHeader } from "../../components/ui/header";
import { DtAutocomplete } from "../../components/ui/autocomplete";
import { hasXPathConfigChanges } from "../../utils/xpathConfigChangeDetector";
import { hasPushbackConfigChanges } from "../../utils/pushbackConfigChangeDetector";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../../utils/bundlingConfigChangeDetector";
import { consumeGlobalSaveHadChanges } from "../../utils/globalSaveStatus";

const EMPTY_CONFIG_IDS: IConfigurationIdEntry[] = [];

const ConfigOtherControls: React.FC = () => {

    const { saveConfigOtherControlsData } = useActions();
    const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
    const xPathData = useSelector((state: RootState) => state.xPathData.data);
    const persistedXPathData = useSelector((state: RootState) => state.xPathData.persistedData);
    const pushbackData = useSelector((state: RootState) => state.configPushbackDefaults.data);
    const persistedPushbackData = useSelector((state: RootState) => state.configPushbackDefaults.persistedData);
    const documentBundles = useSelector((state: RootState) => state.configDocumentBundles.data);
    const persistedDocumentBundles = useSelector((state: RootState) => state.configDocumentBundles.persistedData);
    const eventBundles = useSelector((state: RootState) => state.configEventBundles.data);
    const persistedEventBundles = useSelector((state: RootState) => state.configEventBundles.persistedData);

    const configurationIdsFromRedux = useSelector((state: RootState) => state.configPlugInData.data?.Configuration?.ConfigurationIds ?? EMPTY_CONFIG_IDS);

    // State
    const [otherControls, setOtherControls] = useState<IOtherControl[]>([]);
    const [selectedControl, setSelectedControl] = useState<IOtherControl | null>(null);
    const [selectedControlIdx, setSelectedControlIdx] = useState<number | null>(null);
    const [configurationIds, setConfigurationIds] = useState<IConfigurationIdEntry[]>([]);

    // Sub States 
    const [selectedConfigId, setSelectedConfigId] = useState<string>('');
    const [tabIndex] = useState<number>(1);

    // State Confirmations
    const [alertOpen, setAlertOpen] = useState(false);
    const [saveError, setSaveError] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');
    const [saving, setSaving] = useState<boolean>(false);
    const [isInitialDefaultData, setIsInitialDefaultData] = useState<boolean>(true);

    // Loading Data 
    const hasFetchedData = useRef(false);
    const { data, error, loading } = useConfigurationData(ConfigurationDataType.OTHER_CONTROLS);
    const mData = useMemo(() => data, [data]);

    useEffect(() => {
        if (!hasFetchedData.current && data) {
            const configId = EncompassService.getConfigId() as string;
            setSelectedConfigId(configId);
            setOtherControls(getRefinedData([...data])); // Create a copy of the data
            hasFetchedData.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Handling Loading Error 
    useEffect(() => {
        if (error) {
            openAlert(`Error loading data: ${error}`, 'error');
        }
    }, [error]);

    // Handling Saving Error
    useEffect(() => {
        if (!saveError && data !== null) {
            setOtherControls([...mData]);  // Create a copy to prevent mutations
        }
    }, [data, saveError, mData]);

    // Sync configurationIds from Redux whenever it changes (initial load or after save)
    useEffect(() => {
        setConfigurationIds(configurationIdsFromRedux);
    }, [configurationIdsFromRedux]);

    const saveState = useCallback(() => {
        try {
            if (otherControls.length === 0) return;
            const configPlugInData = store.getState().configPlugInData.data;

            // Create a deep copy to avoid mutation
            const updatedConfigPlugInData = {
                ...configPlugInData,
                Configuration: {
                    ...configPlugInData?.Configuration,
                    OtherControls: otherControls.map(control => ({ ...control }))
                },
            };

            store.dispatch({
                type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
                payload: updatedConfigPlugInData
            });

        } catch (e) {
            console.log("Error Saving State: ", e);
            openAlert(`Error Saving State: ${e}`, 'error');
        }
    }, [otherControls]);

    const selectControl = (index: number, id: string) => {
        setSelectedControlIdx(index);
        setSelectedControl(otherControls?.find(c => c.Id === id) || null);
        const configId = EncompassService.getConfigId() as string;
        setSelectedConfigId(configId);
    }

    useEffect(() => {
        if (otherControls.length > 0) {
            setDefaultValues(otherControls[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otherControls])

    useEffect(() => {
        if (selectedControl != null && otherControls) {
            setOtherControls(prevControls => {
                return prevControls?.map(control =>
                    control.Id === selectedControl.Id ? { ...selectedControl } : control)
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedControl])

    // Save state whenever otherControls changes after initial load
    useEffect(() => {
        if (hasFetchedData.current && otherControls.length > 0) {
            saveState();
        }
    }, [otherControls, saveState])

    //Removing order mavent as part of EN1935
    const getRefinedData = (data: IOtherControl[]) => {
        return data
            .filter((control: any) => {
                return control.FriendlyName?.toLowerCase() !== "order mavent";
            })
            .sort((a, b) => (a?.Id ?? "").localeCompare(b?.Id ?? ""))
            .map(control => ({ ...control }));
    };

    //set DefaultValues on the initial load
    const setDefaultValues = (data: IOtherControl) => {
        if (data?.Id && isInitialDefaultData) {
            selectControl(0, data.Id);
            setIsInitialDefaultData(false);
        }
    }

    // Property Changes
    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => { handleControlPropChange("Enabled", event.target.checked); };
    const handleBehaviorChange = (event: SelectChangeEvent) => { handleControlPropChange("ControlBehavior", event.target.value); };
    const handleCaptionChange = (event: React.ChangeEvent<HTMLInputElement>) => { handleControlPropChange("Caption", event.target.value); };
    const handleControlPropChange = (property: keyof IOtherControl, value: any) => {
        if (selectedControl) {
            const updateControl = { ...selectedControl, [property]: value };
            setSelectedControl(updateControl);
        }
    }

    const save = async () => {
        const globalSaved = consumeGlobalSaveHadChanges();
        try {
            setSaving(true);
            const didSave = Boolean(await saveConfigOtherControlsData(selectedConfigId, otherControls, accessToken, configurationIds));
            const hasXPathChanges = xPathData ? hasXPathConfigChanges(xPathData, persistedXPathData) : false;
            const hasPushbackChanges = pushbackData ? hasPushbackConfigChanges(pushbackData, persistedPushbackData) : false;
            const hasDocumentBundlingChange = documentBundles ? hasDocumentBundlingChanges(documentBundles, persistedDocumentBundles) : false;
            const hasEventBundlingChange = eventBundles ? hasEventBundlingChanges(eventBundles, persistedEventBundles) : false;
            const didSaveAny = didSave || hasXPathChanges || hasPushbackChanges || hasDocumentBundlingChange || hasEventBundlingChange || globalSaved;
            openAlert(didSaveAny ? "Successfully Saved Changes" : "No new changes to save", didSaveAny ? "success" : "info");
            setSaveError(false);
        } catch (e) {
            console.error("Error saving data");
            setSaveError(true);
            if (e instanceof Error) {
                openAlert(`Failed to save data: ${e.message}`, 'error');
            } else {
                openAlert('Failed to save data: Unknown error', 'error');
            }
        } finally {
            setSaving(false);
        }
    }

    // Register save function for header buttons
    useConfigurationSave(() => save());

    const updateSelectedControl = (updatedControl: IOtherControl) => {
        setSelectedControl(updatedControl);
    }

    const handleConfigurationIdsUpdate = useCallback((entries: IConfigurationIdEntry[]) => {
        setConfigurationIds(entries);
        const configPlugInData = store.getState().configPlugInData.data;
        store.dispatch({
            type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
            payload: {
                ...configPlugInData,
                Configuration: {
                    ...configPlugInData?.Configuration,
                    ConfigurationIds: entries,
                },
            }
        });
    }, []);

    const onUpdateUsers = (users: string[]) => {
        setSelectedControl(
            selectedControl ? { ...selectedControl, Users: [...users] } : null,
        )
    }

    // Alert Handling 
    const handleSnackBarClose = (_event: any) => { setAlertOpen(false); }
    const openAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
        setAlertSeverity(severity);
        setAlertMessage(message);
        setAlertOpen(true);
    }

    const memoizedOptions = useMemo(() => otherControls, [otherControls]);
    const memoizedValue = useMemo(
        () =>
            selectedControl
                ? memoizedOptions.find(c => c.Id === selectedControl.Id)
                : undefined,
        [selectedControl, memoizedOptions]
    );

    return (
        <>
            <section>
                <div>
                    {(loading || saving) && <ConfigLoad loading={loading || saving} />}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <DtPageHeader>Other Controls</DtPageHeader>

                    <Stack direction="row" alignItems='center' spacing={2} sx={{ py: 0.8 }}>
                        <DtAutocomplete
                            key={memoizedValue ? memoizedValue.Id : 'no-value'}
                            options={memoizedOptions}
                            getOptionLabel={(option) => option.FriendlyName ?? ""}
                            value={memoizedValue}
                            onChange={(_event, newValue) => {
                                if (newValue) {
                                    const idx = memoizedOptions.findIndex(c => c.Id === newValue.Id);
                                    selectControl(idx, newValue.Id!);
                                }
                            }}
                            textFieldProps={{
                                label: "Select Controls",
                                size: "small",
                                variant: "outlined",
                                fullWidth: true,
                            }}
                            sx={{ minWidth: '16vw' }}
                        />

                        <ControlDetails
                            selectedControl={selectedControl}
                            handleOnEnabledChange={handleCheckboxChange}
                            handleOnBehaviorChange={handleBehaviorChange}
                            handleOnCaptionChange={handleCaptionChange}
                        />
                    </Stack>

                    <Divider />

                    <Box sx={{ padding: 0 }}>
                        <TabControl
                            tabIndex={tabIndex}
                            selectedControl={selectedControl}
                            updateParentControl={updateSelectedControl}
                            sendUserUpdate={onUpdateUsers}
                            configurationIds={configurationIds}
                            updateConfigurationIds={handleConfigurationIdsUpdate}
                        />
                    </Box>

                    <Snackbar
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        open={alertOpen}
                        autoHideDuration={4000}
                        onClose={handleSnackBarClose}
                    >
                        <MuiAlert
                            onClose={handleSnackBarClose}
                            severity={alertSeverity}
                            sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
                            {alertMessage}
                        </MuiAlert>
                    </Snackbar>
                </div>
            </section>
        </>
    )
}

export default ConfigOtherControls;